import React from 'react';

import { GridEventListener, GridRowParams } from '@mui/x-data-grid';

import { reverse } from 'named-urls';

import { GridApiPro } from '@mui/x-data-grid-pro';

import Grid, { defaultRenderers } from 'components/Grid';

import { ColumnDefinition } from 'components/Grid/types';

import EntityRelatedActionsCell from 'components/SharedFormComponents/EntityRelatedActionsCell';

import { EntityRelatedActionsTypeValue } from 'utils/api/modules/entities/entities.types';

import { EntityModules, UserRole, ProtectorType } from 'core/constants';

import { useStore } from 'utils/hooks/store';

import routes from 'core/routes';

import EntityActionCell from 'components/Entities/EntityActionCell';
import { entityCreatedColumn } from 'components/Entities/EntityCreatedColumn';

import { ExtendedUserOperators } from 'components/Grid/operators/User/User';

import { EventsTableProps } from './types';

export const basicEventsColumns = [
  {
    field: 'events__name',
    headerName: 'Name',
    protectorType: ProtectorType.ShortText,
  },
  {
    field: 'events__type',
    headerName: 'Type',
  },
  {
    field: 'events__reporter',
    headerName: 'Reporter',
    renderCell: defaultRenderers.user,
    valueGetter: (value: unknown) => ({ value }),
    filterOperators: ExtendedUserOperators(),
    sortable: false,
    protectorType: ProtectorType.User,
  },
  {
    field: 'events__owner',
    headerName: 'Owner',
    renderCell: defaultRenderers.user,
    valueGetter: (value: unknown) => ({ value }),
    filterOperators: ExtendedUserOperators(),
    sortable: false,
    protectorType: ProtectorType.User,
  },
  {
    field: 'relatedActions',
    headerName: 'Actions',
    renderCell: ({ value }: { value: EntityRelatedActionsTypeValue }) => (
      <EntityRelatedActionsCell relatedActions={value.value || { total: 0, completed: 0 }} />
    ),
    valueFormatter: (value: EntityRelatedActionsTypeValue) =>
      `completed:${value?.value?.completed};total:${value?.value?.total}`,
    nativeFilter: true,
    sortable: false,
  },
  entityCreatedColumn(EntityModules.Events),
  {
    field: 'events__pipeline',
    headerName: 'Pipeline',
    nativeFilter: true,
    sortable: true,
    protectorType: ProtectorType.Pipeline,
  },
  {
    field: 'events__pipelineStage',
    headerName: 'Stage',
    nativeFilter: true,
    sortable: true,
    protectorType: ProtectorType.Stage,
  },
  {
    field: 'formName',
    headerName: 'Form name',
    defaultVisibility: false,
    nativeFilter: true,
  },
] as unknown as ColumnDefinition[];

const EventsTable: React.FunctionComponent<
  EventsTableProps & { gridApiRef: React.MutableRefObject<GridApiPro | null> }
> = ({ onError, onRefreshCallback, gridApiRef }): React.ReactElement => {
  const { session, routing, eventDetails } = useStore();

  const onRowClick: GridEventListener<'rowClick'> = (item: GridRowParams): void => {
    routing.push(reverse(routes.dashboard.events.details, { entityId: item.id }));
  };

  return (
    <Grid
      gridApiRef={gridApiRef}
      basicColumns={
        [
          ...basicEventsColumns,
          {
            field: 'actions',
            type: 'actions',
            headerName: '',
            maxWidth: 50,
            ariaLabel: 'actions_cell',
            renderCell: ({ row, colDef }: { row: { uuid: string }; colDef: ColumnDefinition }) => (
              <EntityActionCell rowData={row} actions={colDef.actions} store={eventDetails} />
            ),
            filterOperators: null,
            sortable: false,
          },
        ] as unknown as ColumnDefinition[]
      }
      entity="events"
      onError={onError}
      dataURL="/api/entities/events/"
      fieldsURL="/api/entities/events/fields/"
      customNoRowsText="No events have been created yet"
      onRowClick={onRowClick}
      onRefreshCallback={onRefreshCallback}
      disableSelectAllColumns
      onlyBasicExport={session.user?.role === UserRole.User}
      sortBy={{ field: 'createdAt', sort: 'desc' }}
      disableColumnSelector={session.user?.role === UserRole.User}
    />
  );
};

export default EventsTable;
